import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query'
import axios from 'axios'
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'
import type {
  CreateCourseReview201,
  CreateCourseReview400,
  CreateCourseReview401,
  CreateCourseReview403,
  CreateCourseReview404,
  CreateCourseReview500,
  CreateCourseReviewBody,
  GetCourseReviews200,
  GetCourseReviews404,
  GetCourseReviews500,
  GetCourseReviewsParams,
  GetLectureMarkdownSignedUrl200,
  GetLectureMarkdownSignedUrl403,
  GetLectureMarkdownSignedUrl404,
  GetLectureVideoSignedUrl200,
  GetLectureVideoSignedUrl403,
  GetLectureVideoSignedUrl404,
  MarkLectureAsCompleted200,
  MarkLectureAsCompleted401,
  MarkLectureAsCompleted403,
  MarkLectureAsCompleted404,
  MarkLectureAsCompleted500,
  UnpublishCourse409
} from './types'


type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;



/**
 * 강의 소유자가 게시된 자신의 강의를 게시 취소 상태로 변경합니다.
 * @summary 게시된 강의 게시 취소
 */
export const unpublishCourse = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<void>> => {
    
    return axios.delete(
      `https://api.ugot.uk/api/courses/${id}/unpublish`,options
    );
  }



export const getUnpublishCourseMutationOptions = <TError = AxiosError<void | UnpublishCourse409>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unpublishCourse>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof unpublishCourse>>, TError,{id: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unpublishCourse>>, {id: number}> = (props) => {
          const {id} = props ?? {};

          return  unpublishCourse(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UnpublishCourseMutationResult = NonNullable<Awaited<ReturnType<typeof unpublishCourse>>>
    
    export type UnpublishCourseMutationError = AxiosError<void | UnpublishCourse409>

    /**
 * @summary 게시된 강의 게시 취소
 */
export const useUnpublishCourse = <TError = AxiosError<void | UnpublishCourse409>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unpublishCourse>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof unpublishCourse>>,
        TError,
        {id: number},
        TContext
      > => {

      const mutationOptions = getUnpublishCourseMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 지정된 강의의 비디오 콘텐츠에 접근하기 위한 시간 제한 Signed URL을 반환합니다. 사용자는 해당 강의에 수강 등록되어 있어야 합니다.
 * @summary 강의 비디오 Signed URL 조회
 */
export const getLectureVideoSignedUrl = (
    courseId: number,
    lectureId: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetLectureVideoSignedUrl200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/courses/${courseId}/lectures/${lectureId}/video-url`,options
    );
  }


export const getGetLectureVideoSignedUrlQueryKey = (courseId: number,
    lectureId: number,) => {
    return [`https://api.ugot.uk/api/courses/${courseId}/lectures/${lectureId}/video-url`] as const;
    }

    
export const getGetLectureVideoSignedUrlInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getLectureVideoSignedUrl>>, TError = AxiosError<GetLectureVideoSignedUrl403 | GetLectureVideoSignedUrl404>>(courseId: number,
    lectureId: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getLectureVideoSignedUrl>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLectureVideoSignedUrlQueryKey(courseId,lectureId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLectureVideoSignedUrl>>> = ({ signal }) => getLectureVideoSignedUrl(courseId,lectureId, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(courseId && lectureId),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getLectureVideoSignedUrl>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLectureVideoSignedUrlInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getLectureVideoSignedUrl>>>
export type GetLectureVideoSignedUrlInfiniteQueryError = AxiosError<GetLectureVideoSignedUrl403 | GetLectureVideoSignedUrl404>

/**
 * @summary 강의 비디오 Signed URL 조회
 */
export const useGetLectureVideoSignedUrlInfinite = <TData = Awaited<ReturnType<typeof getLectureVideoSignedUrl>>, TError = AxiosError<GetLectureVideoSignedUrl403 | GetLectureVideoSignedUrl404>>(
 courseId: number,
    lectureId: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getLectureVideoSignedUrl>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetLectureVideoSignedUrlInfiniteQueryOptions(courseId,lectureId,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetLectureVideoSignedUrlQueryOptions = <TData = Awaited<ReturnType<typeof getLectureVideoSignedUrl>>, TError = AxiosError<GetLectureVideoSignedUrl403 | GetLectureVideoSignedUrl404>>(courseId: number,
    lectureId: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLectureVideoSignedUrl>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLectureVideoSignedUrlQueryKey(courseId,lectureId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLectureVideoSignedUrl>>> = ({ signal }) => getLectureVideoSignedUrl(courseId,lectureId, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(courseId && lectureId),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLectureVideoSignedUrl>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLectureVideoSignedUrlQueryResult = NonNullable<Awaited<ReturnType<typeof getLectureVideoSignedUrl>>>
export type GetLectureVideoSignedUrlQueryError = AxiosError<GetLectureVideoSignedUrl403 | GetLectureVideoSignedUrl404>

/**
 * @summary 강의 비디오 Signed URL 조회
 */
export const useGetLectureVideoSignedUrl = <TData = Awaited<ReturnType<typeof getLectureVideoSignedUrl>>, TError = AxiosError<GetLectureVideoSignedUrl403 | GetLectureVideoSignedUrl404>>(
 courseId: number,
    lectureId: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLectureVideoSignedUrl>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetLectureVideoSignedUrlQueryOptions(courseId,lectureId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 지정된 강의의 마크다운 콘텐츠에 접근하기 위한 시간 제한 Signed URL을 반환합니다. 사용자는 해당 강의에 수강 등록되어 있어야 합니다.
 * @summary 강의 마크다운 Signed URL 조회
 */
export const getLectureMarkdownSignedUrl = (
    courseId: number,
    lectureId: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetLectureMarkdownSignedUrl200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/courses/${courseId}/lectures/${lectureId}/markdown-url`,options
    );
  }


export const getGetLectureMarkdownSignedUrlQueryKey = (courseId: number,
    lectureId: number,) => {
    return [`https://api.ugot.uk/api/courses/${courseId}/lectures/${lectureId}/markdown-url`] as const;
    }

    
export const getGetLectureMarkdownSignedUrlInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>, TError = AxiosError<GetLectureMarkdownSignedUrl403 | GetLectureMarkdownSignedUrl404>>(courseId: number,
    lectureId: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLectureMarkdownSignedUrlQueryKey(courseId,lectureId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>> = ({ signal }) => getLectureMarkdownSignedUrl(courseId,lectureId, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(courseId && lectureId),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLectureMarkdownSignedUrlInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>>
export type GetLectureMarkdownSignedUrlInfiniteQueryError = AxiosError<GetLectureMarkdownSignedUrl403 | GetLectureMarkdownSignedUrl404>

/**
 * @summary 강의 마크다운 Signed URL 조회
 */
export const useGetLectureMarkdownSignedUrlInfinite = <TData = Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>, TError = AxiosError<GetLectureMarkdownSignedUrl403 | GetLectureMarkdownSignedUrl404>>(
 courseId: number,
    lectureId: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetLectureMarkdownSignedUrlInfiniteQueryOptions(courseId,lectureId,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetLectureMarkdownSignedUrlQueryOptions = <TData = Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>, TError = AxiosError<GetLectureMarkdownSignedUrl403 | GetLectureMarkdownSignedUrl404>>(courseId: number,
    lectureId: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLectureMarkdownSignedUrlQueryKey(courseId,lectureId);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>> = ({ signal }) => getLectureMarkdownSignedUrl(courseId,lectureId, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(courseId && lectureId),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLectureMarkdownSignedUrlQueryResult = NonNullable<Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>>
export type GetLectureMarkdownSignedUrlQueryError = AxiosError<GetLectureMarkdownSignedUrl403 | GetLectureMarkdownSignedUrl404>

/**
 * @summary 강의 마크다운 Signed URL 조회
 */
export const useGetLectureMarkdownSignedUrl = <TData = Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>, TError = AxiosError<GetLectureMarkdownSignedUrl403 | GetLectureMarkdownSignedUrl404>>(
 courseId: number,
    lectureId: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLectureMarkdownSignedUrl>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetLectureMarkdownSignedUrlQueryOptions(courseId,lectureId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 현재 로그인한 사용자가 특정 강의를 완료했음을 기록합니다. 이미 완료된 경우에도 성공 응답을 반환할 수 있습니다.
 * @summary 강의 완료 처리
 */
export const markLectureAsCompleted = (
    lectureId: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<MarkLectureAsCompleted200>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/lectures/${lectureId}/complete`,undefined,options
    );
  }



export const getMarkLectureAsCompletedMutationOptions = <TError = AxiosError<MarkLectureAsCompleted401 | MarkLectureAsCompleted403 | MarkLectureAsCompleted404 | MarkLectureAsCompleted500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markLectureAsCompleted>>, TError,{lectureId: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof markLectureAsCompleted>>, TError,{lectureId: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markLectureAsCompleted>>, {lectureId: number}> = (props) => {
          const {lectureId} = props ?? {};

          return  markLectureAsCompleted(lectureId,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type MarkLectureAsCompletedMutationResult = NonNullable<Awaited<ReturnType<typeof markLectureAsCompleted>>>
    
    export type MarkLectureAsCompletedMutationError = AxiosError<MarkLectureAsCompleted401 | MarkLectureAsCompleted403 | MarkLectureAsCompleted404 | MarkLectureAsCompleted500>

    /**
 * @summary 강의 완료 처리
 */
export const useMarkLectureAsCompleted = <TError = AxiosError<MarkLectureAsCompleted401 | MarkLectureAsCompleted403 | MarkLectureAsCompleted404 | MarkLectureAsCompleted500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markLectureAsCompleted>>, TError,{lectureId: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof markLectureAsCompleted>>,
        TError,
        {lectureId: number},
        TContext
      > => {

      const mutationOptions = getMarkLectureAsCompletedMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 특정 강좌에 대한 리뷰를 등록합니다. 사용자는 해당 강좌를 수강 중이어야 합니다.
 * @summary 강의 리뷰 등록
 */
export const createCourseReview = (
    courseId: number,
    createCourseReviewBody: CreateCourseReviewBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<CreateCourseReview201>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/courses/${courseId}/reviews`,
      createCourseReviewBody,options
    );
  }



export const getCreateCourseReviewMutationOptions = <TError = AxiosError<CreateCourseReview400 | CreateCourseReview401 | CreateCourseReview403 | CreateCourseReview404 | CreateCourseReview500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCourseReview>>, TError,{courseId: number;data: CreateCourseReviewBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof createCourseReview>>, TError,{courseId: number;data: CreateCourseReviewBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createCourseReview>>, {courseId: number;data: CreateCourseReviewBody}> = (props) => {
          const {courseId,data} = props ?? {};

          return  createCourseReview(courseId,data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateCourseReviewMutationResult = NonNullable<Awaited<ReturnType<typeof createCourseReview>>>
    export type CreateCourseReviewMutationBody = CreateCourseReviewBody
    export type CreateCourseReviewMutationError = AxiosError<CreateCourseReview400 | CreateCourseReview401 | CreateCourseReview403 | CreateCourseReview404 | CreateCourseReview500>

    /**
 * @summary 강의 리뷰 등록
 */
export const useCreateCourseReview = <TError = AxiosError<CreateCourseReview400 | CreateCourseReview401 | CreateCourseReview403 | CreateCourseReview404 | CreateCourseReview500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCourseReview>>, TError,{courseId: number;data: CreateCourseReviewBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof createCourseReview>>,
        TError,
        {courseId: number;data: CreateCourseReviewBody},
        TContext
      > => {

      const mutationOptions = getCreateCourseReviewMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 특정 강좌에 등록된 리뷰 목록을 페이지네이션하여 조회합니다.
 * @summary 강의 리뷰 목록 조회
 */
export const getCourseReviews = (
    courseId: number,
    params?: GetCourseReviewsParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetCourseReviews200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/courses/${courseId}/reviews`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }


export const getGetCourseReviewsQueryKey = (courseId: number,
    params?: GetCourseReviewsParams,) => {
    return [`https://api.ugot.uk/api/courses/${courseId}/reviews`, ...(params ? [params]: [])] as const;
    }

    
export const getGetCourseReviewsInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getCourseReviews>>, TError = AxiosError<GetCourseReviews404 | GetCourseReviews500>>(courseId: number,
    params?: GetCourseReviewsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourseReviews>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCourseReviewsQueryKey(courseId,params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCourseReviews>>> = ({ signal }) => getCourseReviews(courseId,params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(courseId),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourseReviews>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCourseReviewsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getCourseReviews>>>
export type GetCourseReviewsInfiniteQueryError = AxiosError<GetCourseReviews404 | GetCourseReviews500>

/**
 * @summary 강의 리뷰 목록 조회
 */
export const useGetCourseReviewsInfinite = <TData = Awaited<ReturnType<typeof getCourseReviews>>, TError = AxiosError<GetCourseReviews404 | GetCourseReviews500>>(
 courseId: number,
    params?: GetCourseReviewsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourseReviews>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetCourseReviewsInfiniteQueryOptions(courseId,params,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetCourseReviewsQueryOptions = <TData = Awaited<ReturnType<typeof getCourseReviews>>, TError = AxiosError<GetCourseReviews404 | GetCourseReviews500>>(courseId: number,
    params?: GetCourseReviewsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCourseReviews>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCourseReviewsQueryKey(courseId,params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCourseReviews>>> = ({ signal }) => getCourseReviews(courseId,params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(courseId),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCourseReviews>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCourseReviewsQueryResult = NonNullable<Awaited<ReturnType<typeof getCourseReviews>>>
export type GetCourseReviewsQueryError = AxiosError<GetCourseReviews404 | GetCourseReviews500>

/**
 * @summary 강의 리뷰 목록 조회
 */
export const useGetCourseReviews = <TData = Awaited<ReturnType<typeof getCourseReviews>>, TError = AxiosError<GetCourseReviews404 | GetCourseReviews500>>(
 courseId: number,
    params?: GetCourseReviewsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCourseReviews>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetCourseReviewsQueryOptions(courseId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



